/**
 * LlmCostsCard — admin-only card that surfaces total Claude / OpenAI / Gemini
 * spend for the current month, broken down by category (report analysis,
 * AI chat, investment rationales, market-intelligence PDFs, score recompute).
 *
 * Backed by GET /api/admin/llm-costs which aggregates the `llm_costs`
 * collection.
 */
import React, { useEffect, useState } from "react";
import { apiGet } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Coins, RefreshCcw, FileText, Bot, Sparkles, FilePlus, Activity, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const CATEGORY_ICON = {
  report_analysis: FileText,
  ai_chat: Bot,
  rationale: Sparkles,
  market_pdf: FilePlus,
  company_score: Activity,
};

const fmtUsd = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtTokens = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return String(v);
};

export const LlmCostsCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const res = await apiGet("/admin/llm-costs?days=30");
      setData(res);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <Card className="border bg-card" data-testid="llm-costs-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="font-display text-base flex items-center gap-2">
              <Coins className="h-4 w-4 text-primary" />
              Claude API spend
            </CardTitle>
            <CardDescription className="mt-1">
              Aggregated from the <code className="text-[11px] bg-muted px-1 py-0.5 rounded">llm_costs</code> ledger.
              Token counts are estimated when the SDK doesn&rsquo;t report usage.
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            disabled={refreshing}
            className="h-8 gap-1.5"
            data-testid="llm-costs-refresh"
          >
            {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-7 w-32" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : !data ? (
          <div className="text-xs text-muted-foreground italic">No cost data available yet.</div>
        ) : (
          <>
            {/* Total this month */}
            <div className="flex items-baseline justify-between rounded-lg border bg-muted/30 p-4">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Total this month
                </div>
                <div className="mt-0.5 text-3xl font-display font-semibold tabular" data-testid="llm-costs-month-total">
                  {fmtUsd(data.this_month?.total_usd)}
                </div>
              </div>
              <div className="text-right text-xs text-muted-foreground">
                <div>
                  Last 30 days:&nbsp;
                  <span className="text-foreground font-medium" data-testid="llm-costs-30d-total">
                    {fmtUsd(data.last_n_days?.total_usd)}
                  </span>
                </div>
                <div>
                  All time:&nbsp;
                  <span className="text-foreground font-medium">{fmtUsd(data.all_time?.total_usd)}</span>
                </div>
              </div>
            </div>

            {/* Per-category breakdown */}
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground px-1">
                This month by category
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.entries(data.this_month?.by_category || {})
                  .sort((a, b) => (b[1].cost_usd || 0) - (a[1].cost_usd || 0))
                  .map(([cat, row]) => {
                    const Icon = CATEGORY_ICON[cat] || Coins;
                    return (
                      <div
                        key={cat}
                        className="rounded-md border bg-background/60 p-3"
                        data-testid={`llm-cost-row-${cat}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <span className="text-sm font-medium truncate">{row.label}</span>
                          </div>
                          <span className="tabular text-sm font-semibold">
                            {fmtUsd(row.cost_usd)}
                          </span>
                        </div>
                        <div className="mt-1 text-[10px] text-muted-foreground tabular">
                          {row.calls.toLocaleString()} call{row.calls === 1 ? "" : "s"}
                          {" · "}
                          in {fmtTokens(row.input_tokens)} / out {fmtTokens(row.output_tokens)} tok
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
            <div className="text-[10px] text-muted-foreground italic">
              Pricing assumes Claude Sonnet 4.5 ($3/$15 per 1M tokens). Tokens are
              approximated at ~3.5 chars/token when the SDK doesn&rsquo;t expose
              usage. Re-analysis is skipped automatically when{" "}
              <code className="bg-muted px-1 py-0.5 rounded">analysis_complete=true</code>.
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default LlmCostsCard;
